import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { DeleteButton, List, useDataGrid } from "@refinedev/mui";

// One row of GET /api/v2/admin/gallery — a picture or clip in a player's
// Gallery. A Story Studio row names its story file by `playThroughId` and is
// something the player was shown in play; a v1 row names a story by its integer
// `storyId` and is a liminal background delivered before v1 play went.
type GalleryRecord = {
  id: number;
  userId: number;
  userName: string | null;
  userEmail: string | null;
  storyId: number | null;
  playThroughId: string | null;
  storyTitle: string | null;
  kind: "image" | "video" | string;
  url: string;
  poster: string | null;
  label: string | null;
  persona: string | null;
  shownAt: string | null;
  createdAt: string;
  removedAt: string | null;
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const fieldValue = (
  filters: ReadonlyArray<{ field?: string; value?: unknown } | object>,
  field: string
) => {
  const found = filters.find((f) => "field" in f && f.field === field);
  return found && "value" in found ? found.value : undefined;
};

export const GalleryList = () => {
  // Server-ordered: newest first. Columns are not sortable — the shared data
  // provider sends page/limit only; `kind` and `includeRemoved` ride as filters.
  const { dataGridProps, filters, setFilters } = useDataGrid({});
  const kindFilter = (fieldValue(filters, "kind") as string | undefined) ?? "";
  const includeRemoved = fieldValue(filters, "includeRemoved") === "true";

  const setFilter = (field: string, value: string) => {
    const others = filters.filter((f) => !("field" in f && f.field === field));
    setFilters(
      value ? [...others, { field, operator: "eq", value }] : others,
      "replace"
    );
  };

  const columns = React.useMemo<GridColDef<GalleryRecord>[]>(
    () => [
      {
        field: "url",
        headerName: "Piece",
        minWidth: 150,
        sortable: false,
        display: "flex",
        // A clip shows its poster (its first frame) when it has one, else the
        // film itself, paused on its first frame.
        renderCell: ({ row }) => (
          <Link href={row.url} target="_blank" rel="noreferrer">
            <Box sx={{ position: "relative" }}>
              {row.kind === "video" && !row.poster ? (
                <Box
                  component="video"
                  src={row.url}
                  muted
                  preload="metadata"
                  sx={{
                    width: 128,
                    height: 72,
                    objectFit: "cover",
                    borderRadius: 1,
                    display: "block",
                    bgcolor: "action.hover",
                  }}
                />
              ) : (
                <Box
                  component="img"
                  src={row.kind === "video" ? row.poster! : row.url}
                  alt={row.label ?? row.persona ?? "gallery piece"}
                  loading="lazy"
                  sx={{
                    width: 128,
                    height: 72,
                    objectFit: "cover",
                    borderRadius: 1,
                    display: "block",
                    bgcolor: "action.hover",
                    opacity: row.removedAt ? 0.4 : 1,
                  }}
                />
              )}
              {row.kind === "video" && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 4,
                    bottom: 4,
                    px: 0.5,
                    borderRadius: 0.5,
                    fontSize: 11,
                    color: "common.white",
                    bgcolor: "rgba(0,0,0,0.6)",
                  }}
                >
                  ▶ clip
                </Box>
              )}
            </Box>
          </Link>
        ),
      },
      {
        field: "storyTitle",
        headerName: "Story",
        flex: 1,
        minWidth: 200,
        sortable: false,
        display: "flex",
        // A creator's own draft has no play-through row, so no title.
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {row.storyTitle ?? (
                <span style={{ opacity: 0.6 }}>
                  {row.playThroughId ? "(draft or untitled)" : "(story deleted)"}
                </span>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              <code>{row.playThroughId ?? row.storyId}</code>
            </Typography>
          </Stack>
        ),
      },
      {
        field: "userName",
        headerName: "Player",
        flex: 0.9,
        minWidth: 180,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {row.userName ?? <span style={{ opacity: 0.6 }}>(unknown)</span>}
            </Typography>
            <Tooltip title={row.userEmail ?? ""}>
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.userEmail ?? `user ${row.userId}`}
              </Typography>
            </Tooltip>
          </Stack>
        ),
      },
      {
        field: "label",
        headerName: "Label",
        minWidth: 150,
        sortable: false,
        display: "flex",
        // The engine's own word for the row (v2), or who a v1 frame was
        // rendered around.
        renderCell: ({ row }) => {
          const text = row.label ?? row.persona;
          return text ? (
            <Typography variant="body2" noWrap title={text}>
              {text}
            </Typography>
          ) : (
            <span style={{ opacity: 0.6 }}>-</span>
          );
        },
      },
      {
        field: "source",
        headerName: "Source",
        minWidth: 110,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <Chip
              size="small"
              variant="outlined"
              label={row.playThroughId ? "Studio" : "v1"}
            />
            {row.removedAt && (
              <Tooltip title={`Removed ${formatDateTime(row.removedAt)}`}>
                <Chip size="small" color="warning" label="removed" />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: "shownAt",
        headerName: "Shown",
        minWidth: 190,
        sortable: false,
        display: "flex",
        // When the player was shown it (v2); a v1 row has only when it was
        // first delivered.
        renderCell: ({ row }) => formatDateTime(row.shownAt ?? row.createdAt),
      },
      {
        field: "actions",
        headerName: "Actions",
        align: "right",
        headerAlign: "right",
        minWidth: 100,
        sortable: false,
        display: "flex",
        // Delete takes the piece out of that player's Gallery for good — the
        // file stays (a moment, a wall post or the stage may show the same
        // url). Spelled out in the confirm text so nobody reads this as
        // "purge the file".
        renderCell: ({ row }) =>
          row.removedAt ? null : (
            <DeleteButton
              hideText
              recordItemId={row.id}
              confirmTitle="Take this out of the player's Gallery?"
              confirmOkText="Remove"
            />
          ),
      },
    ],
    []
  );

  return (
    <List canCreate={false}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          What each player was shown in play, newest first. Removing a piece
          takes it out of that player's Gallery on every device; the file
          itself stays.
        </Typography>
      </Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          select
          size="small"
          label="Kind"
          value={kindFilter}
          onChange={(e) => setFilter("kind", e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Pictures and clips</MenuItem>
          <MenuItem value="image">Pictures</MenuItem>
          <MenuItem value="video">Clips</MenuItem>
        </TextField>
        <FormControlLabel
          control={
            <Switch
              checked={includeRemoved}
              onChange={(e) =>
                setFilter("includeRemoved", e.target.checked ? "true" : "")
              }
            />
          }
          label="Show removed"
        />
      </Stack>
      <DataGrid
        {...dataGridProps}
        columns={columns}
        disableColumnFilter
        rowHeight={88}
      />
    </List>
  );
};
